import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Youtube, ChevronLeft, ChevronRight, ExternalLink, Handshake, FolderUp } from "lucide-react";
import {
  useAdminYoutubeChannelsQuery,
  useAdminUpdateYoutubeChannelMutation,
} from "@/generated/graphql";
import { useToast } from "@/hooks/use-toast";
import ImportDriveModal from "@/components/admin/ImportDriveModal";

const formatCount = (count: number | null | undefined): string => {
  if (count == null) return "-";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
};

const AdminChannels = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [partnerOnly, setPartnerOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [importChannel, setImportChannel] = useState<{ id: string; name: string } | null>(null);
  const pageSize = 20;

  const { data, loading, error, refetch } = useAdminYoutubeChannelsQuery({
    variables: {
      page,
      pageSize,
      search: search || undefined,
      partnerOnly: partnerOnly || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const [updateChannel] = useAdminUpdateYoutubeChannelMutation();

  const channels = data?.adminYoutubeChannels?.data || [];
  const totalPages = data?.adminYoutubeChannels?.pageCount || 1;
  const total = data?.adminYoutubeChannels?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleTogglePartner = async (id: string, next: boolean) => {
    try {
      await updateChannel({
        variables: { id, is_autoclipper_partner: next },
      });
      toast({ title: t("admin.channels.toast.updated") });
      refetch();
    } catch (err) {
      console.error("Failed to update channel:", err);
      toast({
        title: t("admin.channels.toast.updateError"),
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Youtube className="w-8 h-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t("admin.channels.title")}</h1>
                <p className="text-muted-foreground">{t("admin.channels.subtitle")}</p>
              </div>
            </div>
            <Badge variant="outline" className="text-red-500 border-red-500">
              ADMIN
            </Badge>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.channels.list")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3 mb-4">
                <Input
                  placeholder={t("admin.channels.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="max-w-sm"
                />
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-2 ml-2">
                  <Switch
                    id="partner-only"
                    checked={partnerOnly}
                    onCheckedChange={(checked) => {
                      setPartnerOnly(checked);
                      setPage(1);
                    }}
                  />
                  <Label htmlFor="partner-only" className="cursor-pointer">
                    {t("admin.channels.partnerOnly")}
                  </Label>
                </div>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[80px]">{t("admin.channels.thumbnail")}</TableHead>
                          <TableHead>{t("admin.channels.name")}</TableHead>
                          <TableHead>{t("admin.channels.handle")}</TableHead>
                          <TableHead>{t("admin.channels.subscribers")}</TableHead>
                          <TableHead>{t("admin.channels.category")}</TableHead>
                          <TableHead className="w-[140px]">{t("admin.channels.partner")}</TableHead>
                          <TableHead className="w-[80px]">{t("admin.channels.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {channels.map((ch) => (
                          <TableRow key={ch.id}>
                            <TableCell>
                              {ch.thumbnail ? (
                                <img
                                  src={ch.thumbnail}
                                  alt={ch.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-bold">
                                  {ch.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{ch.name}</span>
                                {ch.is_autoclipper_partner && (
                                  <span className="inline-flex items-center gap-1 bg-primary/15 text-primary rounded-full px-1.5 py-0.5 text-[10px] font-medium">
                                    <Handshake className="w-3 h-3" />
                                    {t("admin.channels.partnerYes")}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{ch.handle || "-"}</TableCell>
                            <TableCell>{formatCount(ch.subscriber_count)}</TableCell>
                            <TableCell className="text-muted-foreground">{ch.category || "-"}</TableCell>
                            <TableCell>
                              <Switch
                                checked={ch.is_autoclipper_partner}
                                onCheckedChange={(next) => handleTogglePartner(ch.id, next)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {ch.url && (
                                  <a
                                    href={ch.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-primary hover:underline"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setImportChannel({ id: ch.id, name: ch.name })}
                                  className="inline-flex items-center text-primary hover:underline"
                                  title={t("admin.channels.importDrive")}
                                  aria-label={t("admin.channels.importDrive")}
                                >
                                  <FolderUp className="w-4 h-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {channels.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="text-center py-8 text-muted-foreground"
                            >
                              {t("admin.common.noResults")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      {importChannel && (
        <ImportDriveModal
          open={!!importChannel}
          onOpenChange={(open) => !open && setImportChannel(null)}
          channel={importChannel}
        />
      )}
    </Layout>
  );
};

export default AdminChannels;
